import { ThemeEntity } from "@citychallenge/api-public";
import { escapeRegex, debounce } from "@citychallenge/common";
import { observable, action, computed } from "mobx";
import { Translation } from "../i18n";
import { EventModel } from "./event.model";

export class TranslatableTheme {
  public id!: string;
  public name!: string;

  public translation?: Translation;

  @observable
  public active = false;

  @action
  public toggleActive = () => {
    this.active = !this.active;
  };

  constructor(data: { id: string; name: string; translation?: Translation }) {
    Object.assign(this, data);
  }
}

export class EventStore {
  constructor(events: EventModel[] = []) {
    this.events = events;
  }

  @observable
  public events: EventModel[];

  @action
  public addEvent(event: EventModel) {
    this.events.push(event);

    this.events = this.events.slice().sort((a, b) => {
      return a.startDate > b.startDate ? 1 : -1;
    });
  }

  @action
  public setEvents(events: EventModel[]) {
    this.events = events;
  }

  public findById(articleId: string) {
    return this.events.find(({ id }) => {
      return id === articleId;
    });
  }

  @observable
  public searchFilter = "";

  @action
  public setSearchFilter = (value: string) => {
    this.searchFilter = value;
    this.resetThemes();
  };

  public setSearchFilterDebounced = debounce(this.setSearchFilter, 300);

  public resetSearchFilter = () => {
    this.setSearchFilter("");
  };

  @computed
  public get filteredEvents() {
    const { activeThemes, events } = this;

    if (this.searchFilter.length) {
      const regex = new RegExp(escapeRegex(this.searchFilter), "i");
      return events.filter((event) => event.contains(regex));
    }

    if (activeThemes.length) {
      return events.filter((event) => {
        for (const { id } of activeThemes) {
          if (event.theme?.id === id) {
            return true;
          }
        }
        return false;
      });
    }

    return events;
  }

  @computed
  public get themes(): TranslatableTheme[] {
    const allThemes: { [key: string]: ThemeEntity } = {};

    for (const { theme } of this.events) {
      if (!theme) continue;

      if (!theme.id || theme.id in allThemes) continue;
      allThemes[theme.id] = theme;
    }

    return Object.values(allThemes).map((theme, index) => {
      return new TranslatableTheme({
        id: theme.id || `${index}`,
        name: theme.name,
      });
    });
  }

  @computed
  public get activeThemes() {
    return this.themes.filter((t) => t.active);
  }

  @action
  public resetThemes = () => {
    for (const theme of this.themes) {
      theme.active = false;
    }
  };
}
